// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Negotiation findings tab — groups tracked-change findings by KB item and
 * then by preferred / reject / fallback status.
 */
import { type FC, type ReactNode, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, XCircle } from 'lucide-react'
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { MarkdownViewer } from '@/components/ui/markdown-viewer'
import { SchemaFieldsView } from '@/subsystems/knowledge-base/shared/items/SchemaFieldsView'
import type { KbSchema } from '@/subsystems/knowledge-base/shared/types'

import {
  EmptyState,
  PageLink,
  SOURCE_TYPE_LABEL,
  SOURCE_TYPE_LABEL_CLASS,
  SOURCE_TYPE_TEXT_CLASS,
  STATUS_BADGE_CLASS,
  STATUS_LABEL,
  kbTitle,
  normalizeKbId,
  statusIcon,
  worstStatus,
  type NegotiationFinding,
  type NegotiationKbItem,
} from './shared'

interface FindingsTabProps {
  findings: NegotiationFinding[]
  kbItems: NegotiationKbItem[]
  kbSchema?: KbSchema | null
  onOpenPage?: (page: number) => void
  onOpenInDocument?: (annotationId: string) => void
}

type FindingWithId = NegotiationFinding & { _annotationId: string }

export const FindingsTab: FC<FindingsTabProps> = ({
  findings,
  kbItems,
  kbSchema = null,
  onOpenPage,
  onOpenInDocument,
}) => {
  const items = useMemo(() => groupFindingsByKb(findings, kbItems), [findings, kbItems])

  if (!kbItems.length) {
    return <EmptyState message="No negotiation findings configured." />
  }

  const rejectItems = items.filter(i => worstStatus(i.findings) === 'reject')
  const fallbackItems = items.filter(i => worstStatus(i.findings) === 'fallback')
  const preferredItems = items.filter(i => i.findings.length === 0)

  return (
    <section aria-label="Negotiation findings" className="space-y-3">
      <StatusSection
        label="Preferred"
        count={preferredItems.length}
        defaultOpen={false}
        icon={<CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />}
        items={preferredItems}
        kbSchema={kbSchema}
        onOpenPage={onOpenPage}
        onOpenInDocument={onOpenInDocument}
      />
      <StatusSection
        label="Reject"
        count={rejectItems.length}
        defaultOpen={true}
        icon={<XCircle className="h-4 w-4 text-red-500" aria-hidden="true" />}
        items={rejectItems}
        kbSchema={kbSchema}
        onOpenPage={onOpenPage}
        onOpenInDocument={onOpenInDocument}
      />
      <StatusSection
        label="Fallback"
        count={fallbackItems.length}
        defaultOpen={true}
        icon={<AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />}
        items={fallbackItems}
        kbSchema={kbSchema}
        onOpenPage={onOpenPage}
        onOpenInDocument={onOpenInDocument}
      />
    </section>
  )
}

interface KbGroup {
  kb: NegotiationKbItem
  findings: FindingWithId[]
}

function groupFindingsByKb(
  findings: NegotiationFinding[],
  kbItems: NegotiationKbItem[],
): KbGroup[] {
  const findingsWithIds: FindingWithId[] = findings.map((f, i) => ({ ...f, _annotationId: `neg-${i}` }))

  const grouped = new Map<string, FindingWithId[]>()
  for (const f of findingsWithIds) {
    const key = String(f.knowledgeItem)
    const list = grouped.get(key) ?? []
    list.push(f)
    grouped.set(key, list)
  }

  const rows: KbGroup[] = kbItems.map(kb => ({
    kb,
    findings: grouped.get(kb.id) ?? grouped.get(normalizeKbId(kb.id)) ?? [],
  }))

  // Findings whose knowledgeItem isn't in the configured KB get a stub row
  // so they still surface in the Reject/Fallback sections instead of vanishing.
  const seen = new Set<string>()
  rows.forEach(r => {
    seen.add(r.kb.id)
    seen.add(normalizeKbId(r.kb.id))
  })
  for (const [knowledgeItem, list] of grouped) {
    if (seen.has(knowledgeItem) || seen.has(normalizeKbId(knowledgeItem))) continue
    rows.push({
      kb: { id: knowledgeItem, content: list[0]?.summary ?? `KB ${knowledgeItem}` },
      findings: list,
    })
  }
  return rows
}

interface StatusSectionProps {
  label: string
  count: number
  defaultOpen: boolean
  icon: ReactNode
  items: KbGroup[]
  kbSchema?: KbSchema | null
  onOpenPage?: (page: number) => void
  onOpenInDocument?: (annotationId: string) => void
}

const StatusSection: FC<StatusSectionProps> = ({ label, count, defaultOpen, icon, items, kbSchema, onOpenPage, onOpenInDocument }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border rounded">
      <CollapsibleTrigger asChild>
        <div
          className="flex items-center gap-2 py-2 px-3 cursor-pointer hover:bg-muted/50 transition-colors"
          role="button"
          aria-expanded={open}
          tabIndex={0}
        >
          {icon}
          <span className="text-sm font-semibold">{label}</span>
          <Badge variant="outline" className="text-xs px-2 py-0">{count}</Badge>
          <div className="flex-1" />
          {open
            ? <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          }
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {items.length > 0 ? (
          <div className="divide-y border-t" role="list" aria-label={`${label} items`}>
            {items.map(({ kb, findings }) => (
              <KbItemRow key={kb.id} kb={kb} kbSchema={kbSchema} findings={findings} onOpenPage={onOpenPage} onOpenInDocument={onOpenInDocument} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground px-3 py-2 border-t">None</p>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

const KbItemRow: FC<{
  kb: NegotiationKbItem
  kbSchema?: KbSchema | null
  findings: FindingWithId[]
  onOpenPage?: (page: number) => void
  onOpenInDocument?: (annotationId: string) => void
}> = ({ kb, kbSchema, findings, onOpenPage, onOpenInDocument }) => {
  const [open, setOpen] = useState(true)
  const [kbModalOpen, setKbModalOpen] = useState(false)
  const hasFindings = findings.length > 0
  const title = useMemo(() => kbTitle(kb.content), [kb.content])
  const worst = useMemo(() => worstStatus(findings), [findings])

  return (
    <div role="listitem">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className={`flex items-center gap-3 py-2 px-3 ${hasFindings ? 'hover:bg-muted/50' : ''} transition-colors`}>
          {worst ? statusIcon(worst) : <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />}
          <button
            type="button"
            className="text-xs text-primary underline decoration-primary/40 hover:decoration-primary font-mono shrink-0 cursor-pointer"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setKbModalOpen(true) }}
            aria-label={`View KB item ${kb.id}`}
          >
            KB#{kb.id}
          </button>
          <CollapsibleTrigger asChild disabled={!hasFindings}>
            <div
              className={`flex items-center gap-3 flex-1 min-w-0 ${hasFindings ? 'cursor-pointer' : ''}`}
              role="button"
              aria-expanded={hasFindings ? open : undefined}
              aria-label={`${title} — ${hasFindings ? `${findings.length} ${findings.length === 1 ? 'change' : 'changes'}` : 'No concerning changes'}`}
              tabIndex={hasFindings ? 0 : undefined}
            >
              <span className="text-sm font-medium flex-1">{title}</span>
              {hasFindings ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-xs px-2 py-0 ${STATUS_BADGE_CLASS[worst!]}`}>
                    {findings.length}
                  </Badge>
                  {open
                    ? <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    : <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  }
                </div>
              ) : (
                <span className="text-xs text-green-700">No changes</span>
              )}
            </div>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          {hasFindings && (
            <div className="ml-7 pb-2" role="list" aria-label={`Changes for ${title}`}>
              {findings.map((f, i) => (
                <FindingRow
                  key={i}
                  finding={f}
                  onOpenPage={onOpenPage}
                  onOpenInDocument={
                    onOpenInDocument
                      ? () => onOpenInDocument(f._annotationId)
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Dialog open={kbModalOpen} onOpenChange={setKbModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto z-10000">
          <DialogHeader>
            <DialogTitle className="pr-8">#{kb.id} — {title}</DialogTitle>
            <DialogDescription>Knowledge base criteria</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {kbSchema && kb.fields
              ? <SchemaFieldsView schema={kbSchema} fields={kb.fields} />
              : <MarkdownViewer content={kb.content} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const FindingRow: FC<{
  finding: NegotiationFinding
  onOpenPage?: (page: number) => void
  onOpenInDocument?: () => void
}> = ({ finding, onOpenPage, onOpenInDocument }) => {
  const [open, setOpen] = useState(false)

  const summary = finding.summary ?? STATUS_LABEL[finding.status]
  const sectionRef = finding.sectionReference

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div
          className="flex items-center gap-3 py-1.5 px-3 rounded hover:bg-muted/50 cursor-pointer transition-colors"
          role="button"
          aria-expanded={open}
          aria-label={`${summary}${sectionRef ? `, ${sectionRef}` : ''}${finding.pageNumber ? `, page ${finding.pageNumber}` : ''}`}
          tabIndex={0}
        >
          {statusIcon(finding.status, 'h-3.5 w-3.5')}
          <span className="text-sm flex-1">{summary}</span>
          {sectionRef && (
            <Badge variant="secondary" className="font-mono text-xs px-2 py-0">{sectionRef}</Badge>
          )}
          {finding.pageNumber != null && <PageLink page={finding.pageNumber} onOpen={onOpenPage} />}
          {onOpenInDocument && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenInDocument() }}
              className="text-xs text-primary underline decoration-primary/40 hover:decoration-primary cursor-alias"
              title="View this finding in the document"
              aria-label="View this finding in the document"
            >
              View
            </button>
          )}
          {open
            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          }
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-6 mr-3 mb-1 space-y-2 border-l-2 border-muted pl-3 py-1.5">
          {finding.contractText && (
            <div>
              <p className={`text-xs font-medium mb-0.5 ${SOURCE_TYPE_LABEL_CLASS[finding.sourceType] ?? 'text-foreground'}`}>
                {SOURCE_TYPE_LABEL[finding.sourceType] ?? 'Evidence'}
              </p>
              <p className={`text-sm rounded px-2.5 py-1.5 leading-relaxed ${SOURCE_TYPE_TEXT_CLASS[finding.sourceType] ?? 'text-foreground bg-muted/50'}`}>{finding.contractText}</p>
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-foreground mb-0.5">Recommendation</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{finding.recommendation}</p>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
